import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  fetchPtzLimits,
  fetchPtzState,
  ptzAbsoluteMove,
  ptzRelativeMove,
  ptzResetZoom,
  type PtzLimits,
  type PtzState,
} from "../../api/ptz.ts";
import { usesAbsolutePtzMode } from "../../lib/cameraVendor.ts";
import { usePtzAbsoluteStepControl } from "../../lib/usePtzAbsoluteStepControl.ts";
import {
  usePtzContinuousControl,
  type PtzDir,
} from "../../lib/usePtzContinuousControl.ts";

export type FlashbackPtzControlOptions = {
  /** `hikvision` → absolute ONVIF; `seetong` → continuous + presets. */
  vendor?: unknown;
};

export function useFlashbackPtzControl(
  cameraId: string,
  enabled: boolean,
  options: FlashbackPtzControlOptions = {},
) {
  const absoluteMode = usesAbsolutePtzMode(options.vendor);
  const [ptz, setPtz] = useState<PtzState>({ pan: 0, tilt: 0, zoom: 5 });
  const [ptzLimits, setPtzLimits] = useState<PtzLimits | null>(null);
  const [ptzStateLoaded, setPtzStateLoaded] = useState(false);
  const ptzRef = useRef(ptz);

  useEffect(() => {
    ptzRef.current = ptz;
  }, [ptz]);

  useEffect(() => {
    if (!enabled || !cameraId) return undefined;
    let cancelled = false;
    setPtzStateLoaded(false);
    void (async () => {
      try {
        const [state, limits] = await Promise.all([
          fetchPtzState(cameraId),
          fetchPtzLimits(cameraId),
        ]);
        if (cancelled) return;
        setPtz(state);
        setPtzLimits(limits);
        setPtzStateLoaded(true);
      } catch {
        if (!cancelled) setPtzStateLoaded(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cameraId, enabled]);

  const syncFromDriver = useCallback((next: PtzState) => {
    setPtz(next);
  }, []);

  const ptzContinuous = usePtzContinuousControl({
    cameraId,
    operable: enabled && ptzStateLoaded && !absoluteMode,
    onResult: syncFromDriver,
  });

  const ptzAbsolute = usePtzAbsoluteStepControl({
    cameraId,
    operable: enabled && ptzStateLoaded && absoluteMode,
    ptzLimits,
    getPtz: () => ptzRef.current,
    onResult: syncFromDriver,
  });

  const ptzDriver = absoluteMode ? ptzAbsolute : ptzContinuous;

  const ptzZoomSliderMin = ptzLimits ? Math.round(ptzLimits.zoom.min) : 1;
  const ptzZoomSliderMax = ptzLimits ? Math.round(ptzLimits.zoom.max) : 25;

  const atLimit = useMemo(() => {
    if (!ptzLimits) {
      return {
        left: false,
        right: false,
        up: false,
        down: false,
        zoomIn: false,
        zoomOut: false,
      };
    }
    const eps = 0.05;
    const panEndless =
      Math.abs(ptzLimits.pan.max - ptzLimits.pan.min - 360) < 1e-3;
    return {
      left: panEndless ? false : ptz.pan <= ptzLimits.pan.min + eps,
      right: panEndless ? false : ptz.pan >= ptzLimits.pan.max - eps,
      up: ptz.tilt <= ptzLimits.tilt.min + eps,
      down: ptz.tilt >= ptzLimits.tilt.max - eps,
      zoomIn: ptz.zoom >= ptzLimits.zoom.max - eps,
      zoomOut: ptz.zoom <= ptzLimits.zoom.min - eps,
    };
  }, [ptz.pan, ptz.tilt, ptz.zoom, ptzLimits]);

  const atLimitRef = useRef(atLimit);
  useEffect(() => {
    atLimitRef.current = atLimit;
  }, [atLimit]);

  const onPtzPressStart = useCallback(
    (dir: PtzDir) => {
      if (!enabled || !ptzStateLoaded || atLimitRef.current[dir]) return;
      ptzDriver.start(dir);
    },
    [enabled, ptzDriver, ptzStateLoaded],
  );

  const onPtzPressEnd = useCallback(() => {
    void ptzDriver.stop();
  }, [ptzDriver]);

  const onPtzZoomCommit = useCallback(
    (target: number) => {
      const rounded = Math.round(target);
      const cur = Math.round(ptzRef.current.zoom);
      if (rounded === cur) return;
      if (absoluteMode) {
        const s = ptzRef.current;
        void ptzAbsoluteMove(cameraId, {
          pan: s.pan,
          tilt: s.tilt,
          zoom: rounded,
        }).then(setPtz);
        return;
      }
      void ptzRelativeMove(cameraId, { dZoom: rounded - cur }).then(setPtz);
    },
    [absoluteMode, cameraId],
  );

  const resetZoomInFlightRef = useRef(false);

  const onResetZoom = useCallback(async () => {
    if (!enabled || !ptzStateLoaded || resetZoomInFlightRef.current) return;
    resetZoomInFlightRef.current = true;
    await ptzDriver.stop();
    try {
      const next = await ptzResetZoom(cameraId);
      setPtz(next);
    } catch {
      /* ignore */
    } finally {
      resetZoomInFlightRef.current = false;
    }
  }, [cameraId, enabled, ptzDriver, ptzStateLoaded]);

  const syncPtz = useCallback((next: PtzState) => {
    setPtz(next);
  }, []);

  return {
    ptz,
    ptzLimits,
    ptzQueue: ptzDriver,
    atLimit,
    onPtzPressStart,
    onPtzPressEnd,
    ptzZoomSliderMin,
    ptzZoomSliderMax,
    onPtzZoomCommit,
    onResetZoom,
    syncPtz,
    ready: ptzStateLoaded,
    absoluteMode,
  };
}
